import "./ApplyPolyfillsForBrowser";
import * as React from "react";
import { render, unmountComponentAtNode } from "react-dom";

import {
    exposeIntoWindowTestingModeHandleForIntegrationTests,
    isTestingMode,
    setExposedForm,
} from "../Controls/Testing";
import { IForm } from "../Engine/Form/IForm";
import { track } from "../Controls/Internals/Metrics/MetricsProvider";
import { FORM_RENDER } from "../Controls/metricsAction";
import { FormApplication } from "../Controls/FormParts/FormApplication/FormApplication";

import { RawFormResources } from "./RawFormResources";
import { getCandyHub, initializeSentry } from "./Sentry/InitializeSentry";
import { normalizeTargetContainer, TargetContainer } from "./TargetContainer";
import { CandyInjectableModules } from "./CandyInjectableModules";

exposeIntoWindowTestingModeHandleForIntegrationTests();

initializeSentry();

/**
 * Create new Form instance and render Form component
 * @method renderForm
 * @param FormCreator - корневой React-компонент формы для отрисовки
 * @param resources - ресурсы и настройки
 * @param container - элемент, куда будет отрисовываться форма
 * @param formOptions - специфичные настройки формы (обычно {})
 * @return form
 */
// eslint-disable-next-line import/no-default-export
export default async function renderForm(
    FormCreator: (allModules: CandyInjectableModules) => React.ComponentType,
    resources: RawFormResources,
    inContainer: TargetContainer
): Promise<IForm> {
    const container = normalizeTargetContainer(inContainer);
    container.classList.add("candy-container");

    try {
        unmountComponentAtNode(container);
    } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
    }

    if (!isTestingMode()) {
        try {
            const po = new PerformanceObserver(list => {
                const totalDuration = list.getEntries().reduce((acc, it) => {
                    // The long task originated from an event loop task within this browsing context
                    // https://w3c.github.io/longtasks/#dom-performancelongtasktiming-name
                    if (it.name === "self") {
                        return (acc += it.duration);
                    }

                    return acc;
                }, 0);

                if (totalDuration > 10000) {
                    getCandyHub().captureException(new Error(`Long task duration is too long: ${totalDuration}ms`));
                }
            });
            po.observe({ type: "longtask", buffered: true });
        } catch {
            getCandyHub().captureMessage("PerformanceObserver not supported");
        }
    }

    return new Promise<IForm>((resolve, reject) => {
        render(
            <FormApplication
                resources={resources}
                onFormCreated={form => {
                    setExposedForm(form);
                    track("candy", FORM_RENDER);
                    resolve(form);
                }}
                onCreateFormError={error => {
                    reject(error);
                }}
                onFormDestroy={() => {
                    unmountComponentAtNode(container);
                }}>
                {FormCreator}
            </FormApplication>,
            container
        );
    });
}
